import { resolveRoute, useLocation } from "@hakili/app-shell";
import { t } from "@hakili/i18n";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { Accueil } from "./routes/accueil.js";
import { Appointments } from "./routes/appointments.js";
import { Balance } from "./routes/balance.js";
import { Estimation } from "./routes/estimation.js";
import { Reliability } from "./routes/reliability.js";
import { Connexion } from "./routes/connexion.js";
import { DossierDetail } from "./routes/dossier-detail.js";
import { Dossiers } from "./routes/dossiers.js";
import { Prospects } from "./routes/prospects.js";
import { Invitation } from "./routes/invitation.js";
import { Profile } from "./routes/profile.js";
import { Vehicles } from "./routes/vehicles.js";
import { Team } from "./routes/team.js";
import { opensControl } from "./shell/rights.js";
import { useSession } from "./session/useSession.js";
import { DealerShell } from "./shell/DealerShell.js";
import { ECRANS, ROUTES } from "./shell/routes.js";
import { creerDealerStore, type DealerStore } from "./shell/store.js";

/**
 * `P0-DLW-001`: dealer-web, assembled. Widened by `P0-DLW-002` with the route
 * the walk arrives on.
 *
 * **The application takes its origin as a PROP**, as the console's does, so a
 * spec can render it with a stub `fetch` and never touch a document. The entry
 * reads `import.meta.env.VITE_API_ORIGIN` and passes it down; the composition
 * itself knows nothing about the build.
 *
 * **The router is `resolveRoute` over a two-entry table**, not a library:
 * `P0-UIK-013` made the guard a pure function precisely so each app could bind
 * it to whatever renders its outlet. What the table buys here is the PUBLIC
 * route , the invitation link opens with no session, and an `if` on the path
 * would have to re-decide that fact everywhere the gate is consulted.
 */

/**
 * `F1` seated « Rendez-vous », « Solde » and « Fiabilité » as `EcranAVenir`
 * stubs so that all seven rail entries led somewhere while the round ran, and
 * `F5` replaced them with the screens themselves.
 *
 * The map that resolved a stub's title is gone with them, and the swap is what
 * `P1-UIK-025` promises about a marked stub: *"the title and the layout are the
 * FINAL ones: when the screen is built, this wrapper is replaced and nothing
 * around it moves."* The three screens carry the same catalogue keys the stubs
 * used, so the words a founder read on the stub are the words on the screen.
 */

/**
 * `round F`, `F3`: the frame every screen but the inventory sits in.
 *
 * The mockup gives each screen a `<main className="flex-1 overflow-y-auto">`
 * with its own centred column inside. That is one shape for five screens and
 * one exception , « Mes véhicules », whose two columns scroll separately , so
 * the shape is written once here and the exception renders itself.
 */
function Page({
  children,
  large = false,
}: {
  readonly children: ReactNode;
  readonly large?: boolean;
}): React.JSX.Element {
  return (
    <main className="min-w-0 flex-1 overflow-y-auto">
      <div className={`mx-auto w-full px-8 py-8 ${large ? "max-w-5xl" : "max-w-4xl"}`}>{children}</div>
    </main>
  );
}

export interface AppProps {
  readonly origin: string;
  readonly fetchImpl?: typeof globalThis.fetch;
}

export function App({ origin, fetchImpl }: AppProps): React.JSX.Element {
  const session = useSession(origin, fetchImpl);
  const { lireMoi, adopterSession } = session;
  const { path, naviguer } = useLocation();

  // Once per application, never per render , see `DealerShellProps.store`.
  const store = useRef<DealerStore | null>(null);
  store.current ??= creerDealerStore();

  const resolution = resolveRoute(ROUTES, path, ECRANS);
  const ecran = resolution.kind === "render" ? resolution.route.screen : null;
  const publique = resolution.kind === "render" && resolution.route.public === true;

  // The boot read, once. `/me` is what every shell boots on, and until it
  // answers the gate stays in `attente` rather than flashing a login screen at
  // somebody who is already signed in.
  //
  // **It does NOT run on a public route**, and `P0-UIK-024` is why. dealer-web
  // and the console are same-site by `A41`'s design, so the refresh cookie is
  // not withheld between them: on 5 September this boot read answered `401`,
  // refreshed, and brought the founder's CONSOLE session to life on an
  // invitation screen for a dealership they had never joined. A route whose
  // authorisation is its token asks nobody who the visitor is.
  useEffect(() => {
    if (publique) return;
    void lireMoi();
  }, [lireMoi, publique]);

  /**
   * `P0-DLW-004`, `00-SPINE` 6.6's second layer: **this surface admits a
   * dealership's people and nobody else.**
   *
   * The mirror of what the console did to a dealer: a Hakili staff principal
   * handed a valid token rendered this shell, with a header naming a dealership
   * that was not theirs.
   */
  /**
   * `PRICE-GAP-DEALER`, M3: **how many acts wait for this dealership's hand**, asked of the door whenever the person
   * moves (the path) and once the session stands. The shell wears the dot while it is above zero; it clears when
   * the act was done and the door, asked again, no longer lists it. A refused read counts zero , nothing is
   * drawn for a question that could not be asked.
   */
  const [aTraiter, setATraiter] = useState(0);
  const { readAttention } = session;
  useEffect(() => {
    if (publique || session.status !== "connecte") return;
    let vivant = true;
    void readAttention().then((answer) => {
      if (vivant) setATraiter(answer.ok ? answer.value.length : 0);
    });
    return () => {
      vivant = false;
    };
  }, [readAttention, publique, session.status, path]);

  const admis = session.identite === null || session.identite.organisationType === "concessionnaire";
  const refusDeSurface =
    session.identite !== null && !admis ? t("common.surface.refusConcession") : undefined;

  return (
    <DealerShell
      queryClient={session.queryClient}
      store={store.current}
      // A principal this surface will not admit is anonymous TO IT , the shell
      // renders the login and the reason, never a dealership's chrome.
      status={admis ? session.status : "anonyme"}
      // The token is the authorisation, so the gate lets its holder through and
      // the chrome stays off: naming a dealership to somebody who has not joined
      // it yet would be telling a stranger whose invitation this is.
      publique={publique}
      organisation={session.identite?.organisation ?? null}
      nomComplet={session.identite?.fullName ?? null}
      // `F1`: the rail's own person control, « nom · rôle », from `/me`.
      role={session.identite?.role ?? null}
      // `P0-DLW-006`: the chrome's own two links, and the router they drive.
      path={path}
      naviguer={naviguer}
      aTraiter={aTraiter}
      // `A-DLR`, Arc B (S4-6): the bell is live once there is a session , the notices, and her own act of reading one.
      {...(session.status === "connecte"
        ? {
            notifications: {
              rows: session.notices,
              unread: session.unreadNotices,
              onRead: session.readNotice,
              onReadAll: session.readAllNotices,
            },
          }
        : {})}
      // `P0-DLW-007`: the chrome offers only doors this caller can open.
      rights={session.identite?.rights ?? {}}
      /**
       * `OD-18`, closed at `round F5`: « Une idée ? » reaches a door.
       *
       * The shell adds the screen, the door adds the author, and this function
       * is the whole of what the application contributes. **A refusal is
       * THROWN**, because the modal only says « Demande transmise » after its
       * `envoyer` resolves , that is the amendment `P1-UIK-025` took this
       * round, and returning quietly here would put the old lie back.
       */
      submitFeatureRequest={async (demande) => {
        const answer = await session.submitFeatureRequest({
          goal: demande.faire,
          missing: demande.manque,
          benefit: demande.permettrait,
          screenLabel: demande.ecran,
        });
        if (!answer.ok) throw new Error(answer.message);
      }}
      connexion={
        <Connexion
          connecter={session.connecter}
          {...(refusDeSurface === undefined ? {} : { motif: refusDeSurface })}
        />
      }
      deconnecter={session.deconnecter}
    >
      {ecran === "invitation" && resolution.kind === "render" ? (
        <Invitation
          token={resolution.params["token"] ?? ""}
          origin={origin}
          // Spread rather than passed: `exactOptionalPropertyTypes` makes an
          // explicit `undefined` a different thing from an absent property, and
          // the difference is the point , the screen falls back to the global
          // `fetch` only when nobody supplied one.
          {...(fetchImpl === undefined ? {} : { fetchImpl })}
          onAccepted={(accessToken) => {
            // The door opened a session and handed over its token. Adopt it and
            // go to the landing , a screen that dropped the token here would
            // send a person who has just set a password to a login.
            adopterSession(accessToken);
            naviguer("/");
          }}
        />
      ) : ecran === "team" ? (
        /**
         * `P0-DLW-006`. Every door is passed down rather than reached for: the
         * screen holds no client, so a spec drives it through the same seam the
         * browser uses and `useSession` stays the one place a token lives.
         *
         * `selfId` and `mayInviteRoles` come from `/me`. A screen that decided
         * either for itself would be deciding I16 a second time.
         */
        <Page>
          <Team
            selfId={session.identite?.id ?? ""}
            // `FX-1`, `M1` (`F6`): the gestures of Équipe are the responsable's, and
            // the screen learns that from the right `/me` answered , never from the row.
            manages={opensControl(session.identite?.rights["gererEquipeConcession"])}
            mayInviteRoles={session.identite?.mayInviteRoles ?? []}
            listTeam={session.listTeam}
            listVehicles={session.listVehicles}
            invite={session.invite}
            resendInvitation={session.resendInvitation}
            // `round F5`: the panel names the house the key opens.
            organisation={session.identite?.organisation ?? null}
            readDeactivation={session.readDeactivation}
            // `FX-2a`, `M3` (`F7`): the line's own reassignment, passed exactly where
            // the car's own page takes it , the right opens it, never the row's shape.
            {...(opensControl(session.identite?.rights["gererEquipeConcession"])
              ? { assignVehicle: session.assignVehicle }
              : {})}
            deactivate={session.deactivate}
          />
        </Page>
      ) : ecran === "vehicles" ? (
        /**
         * `P0-DLW-010`. `/vehicules` and `/vehicules/:id` render the SAME
         * screen: the id, when the route carries one, opens the car in the
         * split on a wide viewport and as the page itself on a narrow one.
         * Two screens would be two designs of one idea, and the deep link
         * would work in only one of them.
         */
        <Vehicles
          listVehicles={session.listVehicles}
          listPhotoViews={session.listPhotoViews}
          listEquipment={session.listEquipment}
          listPhotos={session.listPhotos}
          uploadPhoto={session.uploadPhoto}
          removePhoto={session.removePhoto}
          papers={{
            readPapers: session.readPapers,
            attachPaper: session.attachPaper,
            declareChassis: session.declareChassis,
          }}
          requests={{
            readRequests: session.readRequests,
            issueProforma: session.issueProforma,
            uploadProforma: session.uploadProforma,
          }}
          publishVehicle={session.publishVehicle}
          readVehicle={session.readVehicle}
          listVehicleMakes={session.listVehicleMakes}
          listVehicleOptions={session.listVehicleOptions}
          editVehicle={session.editVehicle}
          changePrice={session.changePrice}
          withdrawVehicle={session.withdrawVehicle}
          relistVehicle={session.relistVehicle}
          readSettlements={session.readSettlements}
          confirmReceipt={session.confirmReceipt}
          reportNonReceipt={session.reportNonReceipt}
          handoverVehicle={session.handoverVehicle}
          readAvailability={session.readAvailability}
          readReport={session.readReport}
          confirmAvailability={session.confirmAvailability}
          readPriceGap={session.readPriceGap}
          answerPriceGap={session.answerPriceGap}
          createVehicle={session.createVehicle}
          nextReference={session.nextReference}
          readDealership={session.readDealership}
          listCities={session.listCities}
          listCityZones={session.listCityZones}
          // `round F6`, `OD-15`: the hand-over is the MANAGER's. Passed only
          // where `/me` says the caller holds the team's own right , the
          // drawing shows « Changer » to `peutChanger` alone
          // (`hakili-dealer.jsx:2590`), and the door refuses anyone else in
          // its own words regardless of what this screen renders.
          {...(opensControl(session.identite?.rights["gererEquipeConcession"])
            ? { assignVehicle: session.assignVehicle }
            : {})}
          listTeam={session.listTeam}
          naviguer={naviguer}
          {...(resolution.kind === "render" && resolution.params["id"] !== undefined
            ? { selectedId: resolution.params["id"] }
            : {})}
        />
      ) : ecran === "dossiers" ? (
        /** `P1-DLW-048` (G-E1): the dealership's dossier list; the doors are passed down, as every screen's are. */
        <Page large>
          {resolution.kind === "render" && resolution.params["id"] !== undefined ? (
            /** `P1-DLW-052` (B5-b): the dossier's own page, at `/dossiers/:id`. */
            <DossierDetail
              dossierId={resolution.params["id"]}
              readDetail={session.readDossierDetail}
              readDocuments={session.readDocuments}
              issueProforma={session.issueProforma}
              confirmReceipt={session.confirmReceipt}
              naviguer={naviguer}
            />
          ) : (
            <Dossiers
              readDossiers={session.readDossiers}
              issueProforma={session.issueProforma}
              confirmReceipt={session.confirmReceipt}
              naviguer={naviguer}
            />
          )}
        </Page>
      ) : ecran === "prospects" ? (
        /** `P1-DLW-054` (PROSPECTS): the financing leads, read from the dossiers door. */
        <Page large>
          <Prospects
            readDossiers={session.readDossiers}
            listVehicles={session.listVehicles}
            readInterest={session.readVehicleInterest}
          />
        </Page>
      ) : ecran === "appointments" ? (
        /**
         * `round F`, `F5`. `F1` seated these three as `EcranAVenir` stubs so
         * the rail's seven entries all led somewhere while the round was still
         * running; this is the promise `P1-UIK-025` makes about a stub kept ,
         * *"when the screen is built, this wrapper is replaced and nothing
         * around it moves"*. The titles and the summaries are the same
         * catalogue keys the stubs used.
         */
        <Page>
          <Appointments
            listVehicles={session.listVehicles}
            naviguer={naviguer}
            readAvailability={session.readAvailability}
            confirmAvailability={session.confirmAvailability}
          />
        </Page>
      ) : ecran === "estimation" ? (
        <Page large>
          <Estimation
            door={session.estimateCapacity}
            onOpenVehicle={(vehicleId) => {
              naviguer(`/vehicules/${vehicleId}`);
            }}
          />
        </Page>
      ) : ecran === "balance" ? (
        <Page>
          <Balance
            readBalance={session.readBalance}
            declareTopup={session.declareTopup}
            readSettlements={session.readSettlements}
            confirmReceipt={session.confirmReceipt}
            reportNonReceipt={session.reportNonReceipt}
            listVehicles={session.listVehicles}
          />
        </Page>
      ) : ecran === "reliability" ? (
        <Page>
          <Reliability listVehicles={session.listVehicles} />
        </Page>
      ) : ecran === "profile" ? (
        /**
         * `P0-PTA-001`. The doors are passed down, as `Team`'s are, so a spec
         * drives the screen through the same seam the browser uses.
         *
         * `droits` decides whether « Modifier » renders at all , the same
         * reading `P0-DLW-007` gave the navigation. Hiding is a courtesy; the
         * door's own refusal is the enforcement.
         */
        <Page>
          <Profile
            deconnecter={session.deconnecter}
            readDealership={session.readDealership}
            writeAddress={session.writeAddress}
            writeProformaDetails={session.writeProformaDetails}
            droits={session.identite?.rights ?? {}}
            readBankAccount={session.readBankAccount}
            writeBankAccount={session.writeBankAccount}
          />
        </Page>
      ) : (
        /**
         * `round F`, `F2`. The dashboard reads ONE door, `me/vehicles`, and
         * derives every figure and every priority line from it , the mockup's
         * own law at `hakili-dealer.jsx:622-628`. It is passed down like every
         * other, so a spec drives it through the seam the browser uses.
         */
        <Page large>
          <Accueil
            nomComplet={session.identite?.fullName ?? ""}
            organisation={session.identite?.organisation ?? null}
            listVehicles={session.listVehicles}
            readDossiers={session.readDossiers}
            issueProforma={session.issueProforma}
            confirmReceipt={session.confirmReceipt}
            readBalance={session.readBalance}
            readDealership={session.readDealership}
            readAttention={session.readAttention}
            naviguer={naviguer}
          />
        </Page>
      )}
    </DealerShell>
  );
}
